// Shared machinery for the convention / brochure ad kits on the design system
// pages. Each brand supplies its own `draw(ctx, content, surface)`; everything
// here is the part that doesn't change between them: the 2400 × 3000 spec,
// text layout helpers, the JPEG export with print density stamped in, and the
// accompanying alt-text Word file.

export const AD_WIDTH = 2400;
export const AD_HEIGHT = 3000;
export const AD_DPI = 300;           // 2400 × 3000 at 300 DPI is an 8 × 10 in page
export const AD_PREVIEW_SCALE = 0.5; // 1200 × 1500 preview canvas, scaled down by CSS

// ---- Text --------------------------------------------------------------
export const setFont = (ctx, { family, size, weight = 400, style = 'normal', ls = 0 }) => {
  ctx.font = `${style} ${weight} ${size}px ${family}`;
  if ('letterSpacing' in ctx) ctx.letterSpacing = `${ls}px`;
};

// Greedy word wrap that honours hard line breaks. Measured with the live
// canvas font so preview and export break in the same places.
export const wrapLines = (ctx, text, maxWidth) => {
  const lines = [];
  String(text || '').split('\n').forEach((paragraph) => {
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (!words.length) return;
    let line = '';
    words.forEach((word) => {
      const candidate = line ? `${line} ${word}` : word;
      if (line && ctx.measureText(candidate).width > maxWidth) {
        lines.push(line);
        line = word;
      } else {
        line = candidate;
      }
    });
    lines.push(line);
  });
  return lines;
};

// Largest size (stepping down from `max`) at which `text` fits the column in
// at most `maxLines` lines. `font(size)` returns the setFont spec for a size.
// Hard breaks are kept; only overlong lines wrap.
export const fitLines = (ctx, text, { font, max, min, step, maxWidth, maxLines }) => {
  for (let size = max; size >= min; size -= step) {
    setFont(ctx, font(size));
    const lines = wrapLines(ctx, text, maxWidth);
    if (lines.length <= maxLines && lines.every((l) => ctx.measureText(l).width <= maxWidth)) {
      return { size, lines };
    }
  }
  setFont(ctx, font(min));
  return { size: min, lines: wrapLines(ctx, text, maxWidth) };
};

// ---- Shapes ------------------------------------------------------------
export const traceRoundedRect = (ctx, x, y, w, h, radius) => {
  const r = Math.min(radius, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

export const tracePill = (ctx, x, y, w, h) => traceRoundedRect(ctx, x, y, w, h, h / 2);

// ---- Rendering ---------------------------------------------------------
// Paints `draw` onto a fresh canvas at `scale`× the spec size, after the
// webfonts have settled. Returns the canvas and whatever `draw` returned
// (the visible text, in reading order).
export const renderAd = async (draw, content, surface, scale) => {
  if (typeof document === 'undefined') return null;
  if (document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch (e) { /* system fallbacks */ }
  }
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(AD_WIDTH * scale);
  canvas.height = Math.round(AD_HEIGHT * scale);
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);
  const visible = draw(ctx, content, surface);
  return { canvas, visible };
};

// canvas.toBlob() writes a JFIF header with no physical density, so the file
// opens as a 25 × 31 in page at 96 DPI. Stamping 300 DPI in makes it place at
// 8 × 10 in in InDesign, Word, and whatever the convention's designer uses.
export const withJPEGDensity = async (blob, dpi) => {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  // SOI (2) + APP0 marker (2) + length (2) + 'JFIF\0' (5) + version (2) = 13
  const isJFIF = bytes.length > 18
    && bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF && bytes[3] === 0xE0
    && String.fromCharCode(bytes[6], bytes[7], bytes[8], bytes[9]) === 'JFIF';
  if (!isJFIF) return blob;
  bytes[13] = 1;                 // units: dots per inch
  bytes[14] = (dpi >> 8) & 0xFF; // X density
  bytes[15] = dpi & 0xFF;
  bytes[16] = (dpi >> 8) & 0xFF; // Y density
  bytes[17] = dpi & 0xFF;
  return new Blob([bytes], { type: 'image/jpeg' });
};

export const adToJPEG = async (draw, content, surface) => {
  const rendered = await renderAd(draw, content, surface, 1);
  if (!rendered) return null;
  const blob = await new Promise((resolve) => rendered.canvas.toBlob(resolve, 'image/jpeg', 0.92));
  return blob ? withJPEGDensity(blob, AD_DPI) : null;
};

// ---- Alt text (.docx) --------------------------------------------------
// A .docx is a zip of XML parts. The three parts below are the minimum Word
// will open, stored uncompressed so no zip library is needed.
const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    table[n] = c >>> 0;
  }
  return table;
})();

const crc32 = (bytes) => {
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i += 1) c = CRC32_TABLE[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
  return (c ^ 0xFFFFFFFF) >>> 0;
};

const zipStore = (files) => {
  const encoder = new TextEncoder();
  const local = [];
  const central = [];
  let offset = 0;

  files.forEach(({ name, text }) => {
    const nameBytes = encoder.encode(name);
    const data = encoder.encode(text);
    const crc = crc32(data);

    const header = new Uint8Array(30 + nameBytes.length);
    const hv = new DataView(header.buffer);
    hv.setUint32(0, 0x04034b50, true);      // local file header
    hv.setUint16(4, 20, true);              // version needed
    hv.setUint16(6, 0x0800, true);          // flags: UTF-8 names
    hv.setUint16(8, 0, true);               // method: store
    hv.setUint16(10, 0, true);              // mod time
    hv.setUint16(12, 0x21, true);           // mod date (1980-01-01)
    hv.setUint32(14, crc, true);
    hv.setUint32(18, data.length, true);
    hv.setUint32(22, data.length, true);
    hv.setUint16(26, nameBytes.length, true);
    hv.setUint16(28, 0, true);
    header.set(nameBytes, 30);

    const entry = new Uint8Array(46 + nameBytes.length);
    const ev = new DataView(entry.buffer);
    ev.setUint32(0, 0x02014b50, true);      // central directory header
    ev.setUint16(4, 20, true);
    ev.setUint16(6, 20, true);
    ev.setUint16(8, 0x0800, true);
    ev.setUint16(10, 0, true);
    ev.setUint16(12, 0, true);
    ev.setUint16(14, 0x21, true);
    ev.setUint32(16, crc, true);
    ev.setUint32(20, data.length, true);
    ev.setUint32(24, data.length, true);
    ev.setUint16(28, nameBytes.length, true);
    ev.setUint32(42, offset, true);         // local header offset
    entry.set(nameBytes, 46);

    local.push(header, data);
    central.push(entry);
    offset += header.length + data.length;
  });

  const centralSize = central.reduce((n, c) => n + c.length, 0);
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(8, files.length, true);
  endView.setUint16(10, files.length, true);
  endView.setUint32(12, centralSize, true);
  endView.setUint32(16, offset, true);

  return new Blob([...local, ...central, end], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
};

const xmlText = (str) => String(str)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const docxParagraph = (text, { bold = false, size = 22 } = {}) =>
  `<w:p><w:pPr><w:spacing w:after="160"/></w:pPr><w:r><w:rPr>${bold ? '<w:b/>' : ''}` +
  `<w:sz w:val="${size}"/></w:rPr><w:t xml:space="preserve">${xmlText(text)}</w:t></w:r></w:p>`;

// `lines` are the lines the renderer actually drew, so the Word file can't
// drift from the JPEG. `alt` is the one-paragraph description built from them.
export const altTextDocx = ({ title, alt, lines, link }) => {
  const body = [
    docxParagraph(title, { bold: true, size: 32 }),
    docxParagraph('Alt text', { bold: true }),
    docxParagraph(alt),
    docxParagraph('Text visible in the image', { bold: true }),
    ...lines.map((line) => docxParagraph(line)),
    docxParagraph('Hyperlink', { bold: true }),
    docxParagraph(link || ''),
  ].join('');

  return zipStore([
    {
      name: '[Content_Types].xml',
      text: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
        '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
        '<Default Extension="xml" ContentType="application/xml"/>' +
        '<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>' +
        '</Types>',
    },
    {
      name: '_rels/.rels',
      text: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>' +
        '</Relationships>',
    },
    {
      name: 'word/document.xml',
      text: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">' +
        `<w:body>${body}<w:sectPr><w:pgSz w:w="12240" w:h="15840"/>` +
        '<w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440" w:header="720" w:footer="720" w:gutter="0"/>' +
        '</w:sectPr></w:body></w:document>',
    },
  ]);
};

export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
